/**
 * Responsive Position Calculator
 *
 * Converts absolute pixel positions to responsive CSS-first positioning.
 * Enables seamless viewport switching without position recalculation.
 *
 * Architecture Decision: Use percentage-based positioning with viewport-aware
 * calculations to maintain component relationships across all breakpoints.
 */

import { Position, Size } from "@/types";
import { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";

export interface ResponsivePositionConfig {
  basePosition: Position;
  baseViewport: ResponsiveBreakpoint;
  targetViewport: ResponsiveBreakpoint;
  containerDimensions: Size;
}

export interface ResponsivePositionResult {
  position: Position;
  cssPosition: {
    left: string;
    top: string;
    zIndex: number;
  };
  percentagePosition: {
    leftPercent: number;
    topPercent: number;
  };
  scaleFactor: number;
}

/**
 * Calculate responsive position for a component across viewports
 *
 * Strategy:
 * 1. Convert absolute pixels to percentages based on base viewport
 * 2. Apply viewport ratio scaling for proportional adaptation
 * 3. Maintain aspect ratios and component relationships
 * 4. Generate CSS custom properties for dynamic updates
 */
export const calculateResponsivePosition = (
  config: ResponsivePositionConfig
): ResponsivePositionResult => {
  const { basePosition, baseViewport, targetViewport, containerDimensions } = config;

  // Calculate viewport scale factor
  const widthRatio = targetViewport.width / baseViewport.width;
  const heightRatio = targetViewport.height / baseViewport.height;

  // Use the smaller ratio to maintain aspect ratio
  const scaleFactor = Math.min(widthRatio, heightRatio);

  // Convert base position to percentages
  const leftPercent = (basePosition.x / baseViewport.width) * 100;
  const topPercent = (basePosition.y / baseViewport.height) * 100;

  // Calculate scaled position in target viewport
  const scaledX = basePosition.x * scaleFactor;
  const scaledY = basePosition.y * scaleFactor;

  // Constrain within target viewport bounds
  const constrainedX = Math.max(0, Math.min(scaledX, targetViewport.width));
  const constrainedY = Math.max(0, Math.min(scaledY, targetViewport.height));

  return {
    position: {
      x: constrainedX,
      y: constrainedY,
      z: basePosition.z,
    },
    cssPosition: {
      left: `${leftPercent}%`,
      top: `${topPercent}%`,
      zIndex: basePosition.z,
    },
    percentagePosition: {
      leftPercent,
      topPercent,
    },
    scaleFactor,
  };
};

/**
 * Calculate responsive size with aspect ratio preservation
 */
export const calculateResponsiveSize = (
  baseSize: Size,
  baseViewport: ResponsiveBreakpoint,
  targetViewport: ResponsiveBreakpoint
): Size & { cssSize: { width: string; height: string } } => {
  const widthRatio = targetViewport.width / baseViewport.width;
  const heightRatio = targetViewport.height / baseViewport.height;
  const scaleFactor = Math.min(widthRatio, heightRatio);

  const scaledWidth = baseSize.width * scaleFactor;
  const scaledHeight = baseSize.height * scaleFactor;

  // Calculate percentage-based sizes
  const widthPercent = (baseSize.width / baseViewport.width) * 100;
  const heightPercent = (baseSize.height / baseViewport.height) * 100;

  return {
    width: scaledWidth,
    height: scaledHeight,
    cssSize: {
      width: `${widthPercent}%`,
      height: `${heightPercent}%`,
    },
  };
};

/**
 * Convert absolute position to CSS Grid coordinates
 *
 * Uses CSS Grid for precise component placement while maintaining
 * responsive behavior through grid-template-columns/rows.
 */
export const positionToGridCoordinates = (
  position: Position,
  gridSize: number,
  viewport: ResponsiveBreakpoint
): {
  gridColumn: string;
  gridRow: string;
  cssGrid: string;
} => {
  // Calculate grid cell positions
  const columnStart = Math.floor(position.x / gridSize) + 1;
  const rowStart = Math.floor(position.y / gridSize) + 1;

  return {
    gridColumn: `${columnStart}`,
    gridRow: `${rowStart}`,
    cssGrid: `${rowStart} / ${columnStart}`,
  };
};

/**
 * Calculate proportional value for responsive property inheritance
 *
 * When a viewport doesn't have a specific value, this calculates
 * the proportional equivalent from a larger viewport.
 */
export const calculateProportionalValue = (
  sourceValue: number,
  sourceViewport: ResponsiveBreakpoint,
  targetViewport: ResponsiveBreakpoint,
  propertyType: 'position' | 'size' | 'spacing'
): number => {
  const widthRatio = targetViewport.width / sourceViewport.width;
  const heightRatio = targetViewport.height / sourceViewport.height;

  switch (propertyType) {
    case 'position':
    case 'spacing':
      // Use average ratio for spacing properties
      return sourceValue * ((widthRatio + heightRatio) / 2);
    case 'size':
      // Use minimum ratio to maintain aspect ratio
      return sourceValue * Math.min(widthRatio, heightRatio);
    default:
      return sourceValue;
  }
};

/**
 * Generate CSS custom properties for dynamic responsive behavior
 *
 * Creates CSS variables that can be updated via JavaScript for
 * real-time viewport transitions without DOM manipulation.
 */
export const generateResponsiveCSSProperties = (
  componentId: string,
  position: Position,
  size: Size,
  viewport: ResponsiveBreakpoint
): Record<string, string> => {
  const leftPercent = (position.x / viewport.width) * 100;
  const topPercent = (position.y / viewport.height) * 100;
  const widthPercent = (size.width / viewport.width) * 100;
  const heightPercent = (size.height / viewport.height) * 100;

  return {
    [`--component-${componentId}-left`]: `${leftPercent}%`,
    [`--component-${componentId}-top`]: `${topPercent}%`,
    [`--component-${componentId}-width`]: `${widthPercent}%`,
    [`--component-${componentId}-height`]: `${heightPercent}%`,
    [`--component-${componentId}-z`]: String(position.z),
  };
};

/**
 * Calculate viewport bounds for component culling
 *
 * Determines which components are visible in the current viewport
 * for performance optimization via spatial indexing.
 */
export interface ViewportBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const isComponentInViewport = (
  position: Position,
  size: Size,
  viewportBounds: ViewportBounds,
  buffer: number = 100
): boolean => {
  const componentRight = position.x + size.width;
  const componentBottom = position.y + size.height;
  const viewportRight = viewportBounds.x + viewportBounds.width + buffer;
  const viewportBottom = viewportBounds.y + viewportBounds.height + buffer;

  return (
    position.x < viewportRight &&
    componentRight > viewportBounds.x - buffer &&
    position.y < viewportBottom &&
    componentBottom > viewportBounds.y - buffer
  );
};

/**
 * Calculate responsive canvas dimensions
 *
 * Enhanced version of calculateCanvasContentBounds that accounts for
 * responsive positioning and viewport scaling.
 */
export const calculateResponsiveCanvasBounds = (
  components: Array<{ position: Position; size: Size }>,
  viewport: ResponsiveBreakpoint,
  padding: number = 100
): Size => {
  if (components.length === 0) {
    return { width: viewport.width, height: viewport.height };
  }

  let maxWidth = viewport.width;
  let maxHeight = viewport.height;

  components.forEach((component) => {
    const rightEdge = component.position.x + component.size.width;
    const bottomEdge = component.position.y + component.size.height;

    if (rightEdge > maxWidth) maxWidth = rightEdge;
    if (bottomEdge > maxHeight) maxHeight = bottomEdge;
  });

  return {
    width: Math.max(maxWidth + padding, viewport.width),
    height: Math.max(maxHeight + padding, viewport.height),
  };
};

/**
 * Preserve relative positioning during viewport transitions
 *
 * Maintains spatial relationships between components when
 * switching viewports (e.g., "Component B is 20px right of Component A").
 */
export interface ComponentRelationship {
  componentId: string;
  relativeToId: string;
  offsetX: number;
  offsetY: number;
}

export const calculateRelativePositions = (
  components: Array<{ id: string; position: Position; size: Size }>
): ComponentRelationship[] => {
  const relationships: ComponentRelationship[] = [];

  // Find spatial relationships between components
  components.forEach((component, index) => {
    // Find nearest neighbor to the left
    const leftNeighbor = components.find(
      (c, i) =>
        i !== index &&
        c.position.x < component.position.x &&
        Math.abs(c.position.y - component.position.y) < 50
    );

    if (leftNeighbor) {
      relationships.push({
        componentId: component.id,
        relativeToId: leftNeighbor.id,
        offsetX: component.position.x - (leftNeighbor.position.x + leftNeighbor.size.width),
        offsetY: component.position.y - leftNeighbor.position.y,
      });
    }
  });

  return relationships;
};

/**
 * Apply viewport-specific constraints
 *
 * Ensures components remain within viewport bounds and
 * respects responsive design constraints.
 */
export const applyResponsiveConstraints = (
  position: Position,
  size: Size,
  viewport: ResponsiveBreakpoint,
  constraints?: {
    minX?: number;
    maxX?: number;
    minY?: number;
    maxY?: number;
    minWidth?: number;
    maxWidth?: number;
    minHeight?: number;
    maxHeight?: number;
  }
): { position: Position; size: Size } => {
  const { minX = 0, maxX = viewport.width, minY = 0, maxY = viewport.height } = constraints || {};

  const constrainedPosition: Position = {
    x: Math.max(minX, Math.min(position.x, maxX - size.width)),
    y: Math.max(minY, Math.min(position.y, maxY - size.height)),
    z: position.z,
  };

  const constrainedSize: Size = {
    width: Math.max(
      constraints?.minWidth || 20,
      Math.min(size.width, (constraints?.maxWidth || viewport.width) - constrainedPosition.x)
    ),
    height: Math.max(
      constraints?.minHeight || 20,
      Math.min(size.height, (constraints?.maxHeight || viewport.height) - constrainedPosition.y)
    ),
  };

  return {
    position: constrainedPosition,
    size: constrainedSize,
  };
};
